import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  fetchCustodySummary,
  fetchImpBalances,
  fetchImpTxns,
  fetchSitesForEpsf,
  fetchStudiesForEpsf,
  loadEpsfContext,
  saveEpsfContext,
} from "./epsfService";
import utils from "../../../utils/utils";
import {
  renderConditionBadge,
  renderReceiptStatusBadge,
} from "./epsfBadges";
import { useUserData } from "../../../hooks/useUserData";
import {
  getEpsfGrantedSiteScope,
  getEpsfGrantedStudyIds,
  getExternalEntityAccessRows,
  isSponsorCroSession,
} from "../../../utils/sponsorCroAccess";

export const EMPTY_OPTION = { value: "", label: "" };

const EpsfStudySiteContext = createContext(null);

export function findOption(options, value) {
  const list = Array.isArray(options) ? options : [];
  const v = value == null ? "" : String(value);
  return list.find((o) => String(o?.value ?? "") === v) || null;
}

function useEpsfStudySiteState() {
  const { userData } = useUserData();
  const isExternal = isSponsorCroSession(userData);
  const saved = loadEpsfContext();
  const [studyId, setStudyId] = useState(saved.studyId);
  const [siteId, setSiteId] = useState(saved.siteId);
  const [studyOptions, setStudyOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  const externalGrantKey = useMemo(() => {
    if (!isExternal) return "";
    return getExternalEntityAccessRows(userData)
      .map(
        (row) =>
          `${String(row?.Module ?? row?.module ?? "")}|${String(row?.StudyId ?? row?.studyId ?? "")}|${String(row?.SiteId ?? row?.siteId ?? "")}|${String(row?.Permissions ?? row?.permissions ?? "")}|${String(row?.IsActive ?? row?.isActive ?? "")}`,
      )
      .sort()
      .join(";");
  }, [isExternal, userData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const studies = await fetchStudiesForEpsf();
      if (cancelled) return;
      let opts = studies.options || [];
      if (isExternal) {
        const granted = getEpsfGrantedStudyIds(userData);
        if (granted.size > 0) {
          opts = opts.filter((o) => {
            const keys = [o.value, o.studyIdRef, o.studyScheduleId]
              .map((v) => String(v || "").trim().toLowerCase())
              .filter(Boolean);
            return keys.some((k) => granted.has(k));
          });
        } else {
          opts = [];
        }
      }
      setStudyOptions((prev) => {
        const prevKey = (prev || []).map((o) => o.value).join(",");
        const nextKey = opts.map((o) => o.value).join(",");
        return prevKey === nextKey ? prev : opts;
      });
      if (opts.length) {
        const stillValid = opts.some(
          (o) => String(o.value) === String(studyId || ""),
        );
        if (!stillValid) {
          setStudyId(opts[0].value);
          setSiteId("");
        }
      } else if (isExternal) {
        setStudyId((prev) => (prev ? "" : prev));
        setSiteId((prev) => (prev ? "" : prev));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- studyId only for validity check
  }, [isExternal, userData?.StaffId, externalGrantKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!studyId) {
        setSiteOptions((prev) => (prev.length ? [] : prev));
        return;
      }
      const sites = await fetchSitesForEpsf(studyId);
      if (cancelled) return;
      let opts = sites.options || [];
      if (isExternal) {
        const { siteIds, wholeStudy } = getEpsfGrantedSiteScope(
          userData,
          studyId,
        );
        if (!wholeStudy && siteIds.size > 0) {
          opts = opts.filter((o) =>
            siteIds.has(String(o.value || "").trim().toLowerCase()),
          );
        }
      }
      setSiteOptions((prev) => {
        const prevKey = (prev || []).map((o) => o.value).join(",");
        const nextKey = opts.map((o) => o.value).join(",");
        return prevKey === nextKey ? prev : opts;
      });
      if (siteId && !opts.some((o) => String(o.value) === String(siteId))) {
        setSiteId(opts[0]?.value || "");
      } else if (!siteId && opts.length === 1) {
        setSiteId(opts[0].value);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- siteId read for validation only
  }, [studyId, isExternal, userData?.StaffId, externalGrantKey]);

  useEffect(() => {
    if (studyId || siteId) saveEpsfContext({ studyId, siteId });
  }, [studyId, siteId]);

  const onStudyChange = useCallback((nextStudyId) => {
    setStudyId(nextStudyId || "");
    setSiteId("");
  }, []);

  return useMemo(
    () => ({
      studyId,
      setStudyId: onStudyChange,
      siteId,
      setSiteId,
      studyOptions,
      siteOptions,
    }),
    [studyId, onStudyChange, siteId, studyOptions, siteOptions],
  );
}

/** Workspace-level provider — one study/site for all ePSF screens. */
export function EpsfStudySiteProvider({ children }) {
  const value = useEpsfStudySiteState();
  return (
    <EpsfStudySiteContext.Provider value={value}>
      {children}
    </EpsfStudySiteContext.Provider>
  );
}

export function useEpsfStudySite() {
  const ctx = useContext(EpsfStudySiteContext);
  if (!ctx) {
    throw new Error(
      "useEpsfStudySite must be used within EpsfStudySiteProvider",
    );
  }
  return ctx;
}

/** Safe for shared ISF screens that may render outside the ePSF workspace. */
export function useEpsfStudySiteOptional() {
  return useContext(EpsfStudySiteContext);
}

function splitOptionLines(opt) {
  const label = String(opt?.label || "").trim();
  if (!label) return { primary: "—", secondary: "" };
  const parts = label.split(/\s+[–—-]\s+/);
  if (parts.length >= 2) {
    return {
      primary: parts[0].trim(),
      secondary: parts.slice(1).join(" – ").trim(),
    };
  }
  return { primary: label, secondary: "" };
}

function placeCrumbPopupNearAnchor(anchorEl, { width = 280, height = 300 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect || (!rect.width && !rect.height)) return { top: 8, left: 8 };
  const gap = 6;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuW = Math.max(140, Number(width) || 280);
  const menuH = Math.max(100, Number(height) || 300);

  let left = rect.left;
  let top = rect.bottom + gap;

  if (top + menuH > vh - pad) {
    const above = rect.top - menuH - gap;
    if (above >= pad && above + menuH >= rect.top - 12) {
      top = above;
    } else {
      top = Math.max(pad, vh - pad - menuH);
    }
  }

  if (left + menuW > vw - pad) {
    left = Math.max(pad, Math.min(rect.right - menuW, vw - pad - menuW));
  }
  if (left < pad) left = pad;

  top = Math.min(Math.max(pad, top), Math.max(pad, vh - menuH - pad));
  left = Math.min(Math.max(pad, left), Math.max(pad, vw - menuW - pad));
  return { top, left };
}

/** Kit-mapping style floating picker (search + list + clear). */
function EpsfCrumbPicker({
  title,
  value,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  onSelect,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const selected = (options || []).find(
    (o) => String(o.value) === String(value || ""),
  );
  const display = selected?.label || placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => searchRef.current?.focus?.(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = wrapRef.current;
      const menu = menuRef.current;
      if (!anchor) return;
      setMenuPos(
        placeCrumbPopupNearAnchor(anchor, {
          width: menu?.offsetWidth || 280,
          height: menu?.offsetHeight || 300,
        }),
      );
    };
    const raf = window.requestAnimationFrame(place);
    const onScrollOrResize = () => place();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  const openMenu = (event) => {
    event.stopPropagation();
    if (disabled) return;
    setSearch("");
    const anchor = event.currentTarget || wrapRef.current;
    setMenuPos(placeCrumbPopupNearAnchor(anchor, { width: 280, height: 300 }));
    setOpen((prev) => !prev);
  };

  const q = String(search || "")
    .trim()
    .toLowerCase();
  const filtered = (options || []).filter((opt) => {
    if (!q) return true;
    const hay = `${opt.label || ""} ${opt.value || ""}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="epsf-kit-assign epsf-crumb-pick" ref={wrapRef}>
      <button
        type="button"
        className={`epsf-kit-assign__trigger${selected ? " is-set" : ""}`}
        onClick={openMenu}
        disabled={disabled}
        title={display}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>
          <em className="epsf-crumb-pick__kind">{title}</em>
          {display}
        </span>
        <i className="fas fa-pencil-alt" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="epsf-kit-assign__menu"
              role="dialog"
              aria-label={title}
              style={{
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="epsf-kit-assign__menu-title">{title}</div>
              <input
                ref={searchRef}
                type="search"
                className="form-control form-control-sm epsf-kit-assign__search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="epsf-kit-assign__list" role="listbox">
                {filtered.map((opt) => {
                  const isSelected = String(opt.value) === String(value || "");
                  const lines = splitOptionLines(opt);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`epsf-kit-assign__item${
                        isSelected ? " is-selected" : ""
                      }`}
                      onClick={() => {
                        setOpen(false);
                        onSelect?.(opt.value || "");
                      }}
                    >
                      <span className="epsf-kit-assign__item-main">
                        <strong>{lines.primary}</strong>
                        {lines.secondary ? (
                          <span>{lines.secondary}</span>
                        ) : null}
                      </span>
                      {isSelected ? (
                        <i className="fas fa-check" aria-hidden="true" />
                      ) : null}
                    </button>
                  );
                })}
                {!filtered.length ? (
                  <div className="epsf-kit-assign__empty">{emptyText}</div>
                ) : null}
              </div>
              <div className="epsf-kit-assign__footer">
                <button
                  type="button"
                  className="epsf-kit-assign__clear"
                  disabled={!value}
                  onClick={() => {
                    setOpen(false);
                    onSelect?.("");
                  }}
                >
                  Clear selection
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** Compact Study / Site selectors for the breadcrumb trailing slot. */
export function EpsfCrumbStudySite() {
  const ctx = useEpsfStudySiteOptional();
  if (!ctx) return null;

  const {
    studyId,
    setStudyId,
    siteId,
    setSiteId,
    studyOptions,
    siteOptions,
  } = ctx;

  return (
    <div className="epsf-crumb-context" aria-label="Study and site">
      <EpsfCrumbPicker
        title="Study"
        value={studyId}
        options={studyOptions || []}
        placeholder="Select study"
        searchPlaceholder="Search study…"
        emptyText="No matching study"
        onSelect={(next) => setStudyId(next || "")}
      />
      <EpsfCrumbPicker
        title="Site"
        value={siteId}
        options={siteOptions || []}
        placeholder="Select site"
        searchPlaceholder="Search site…"
        emptyText={studyId ? "No matching site" : "Select a study first"}
        disabled={!studyId}
        onSelect={(next) => setSiteId(next || "")}
      />
    </div>
  );
}

/** @deprecated Use EpsfCrumbStudySite — left-rail Study/Site removed. */
export function EpsfNavStudySite() {
  return <EpsfCrumbStudySite />;
}

/** Study/site selection lives on the ePSF breadcrumb row. */
export function EpsfContextBar() {
  return null;
}

export function EpsfCustodyStrip({ custody }) {
  const c = custody || {
    received: 0,
    onHand: 0,
    dispensed: 0,
    returned: 0,
    destroyed: 0,
  };
  return (
    <div className="epsf-custody" aria-label="IMP custody summary">
      <div className="epsf-custody__stat">
        <span className="epsf-custody__value">{c.received}</span>
        <span className="epsf-custody__label">Received</span>
      </div>
      <div className="epsf-custody__stat is-emphasis">
        <span className="epsf-custody__value">{c.onHand}</span>
        <span className="epsf-custody__label">On hand</span>
      </div>
      <div className="epsf-custody__stat">
        <span className="epsf-custody__value">{c.dispensed}</span>
        <span className="epsf-custody__label">Dispensed</span>
      </div>
      <div className="epsf-custody__stat">
        <span className="epsf-custody__value">{c.returned}</span>
        <span className="epsf-custody__label">Returned</span>
      </div>
      <div className="epsf-custody__stat">
        <span className="epsf-custody__value">{c.destroyed}</span>
        <span className="epsf-custody__label">Destroyed</span>
      </div>
    </div>
  );
}

export function useEpsfLedger(studyId, siteId, txnType) {
  const [custody, setCustody] = useState({
    received: 0,
    onHand: 0,
    dispensed: 0,
    returned: 0,
    destroyed: 0,
  });
  const [txns, setTxns] = useState([]);
  const [balances, setBalances] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(studyId && siteId));

  const refresh = useCallback(async () => {
    if (!studyId || !siteId) {
      setCustody({
        received: 0,
        onHand: 0,
        dispensed: 0,
        returned: 0,
        destroyed: 0,
      });
      setTxns([]);
      setBalances([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [summary, list, bal] = await Promise.all([
        fetchCustodySummary(studyId, siteId),
        fetchImpTxns(studyId, siteId, txnType || null),
        fetchImpBalances(studyId, siteId),
      ]);
      if (summary.success) setCustody(summary.custody);
      if (list.success) setTxns(list.rows || []);
      if (bal.success) setBalances(bal.rows || []);
      if (!summary.success && summary.message) setError(summary.message);
    } finally {
      setLoading(false);
    }
  }, [studyId, siteId, txnType]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { custody, txns, balances, error, setError, loading, refresh };
}

export function formatTxnWhen(row) {
  const raw = row.TxnAt || row.txnAt || row.CreatedAt || "";
  if (!raw) return "";
  return utils.formatDateTimeDisplay(raw);
}

export function sanitizeEpsfDisplayLabel(raw) {
  return String(raw || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/â€”/g, "-")
    .replace(/â€“/g, "-")
    .replace(/\u2014|\u2013/g, "-")
    .replace(/[\u00A0\s]+/g, " ")
    .trim();
}

export function renderImpProductCell(row, { as: As = "div", className, ...rest } = {}) {
  const code = sanitizeEpsfDisplayLabel(row.ProductCode || row.productCode || "");
  const name = sanitizeEpsfDisplayLabel(row.ProductName || row.productName || "");
  if (!code && !name) return "—";
  const classes = ["epsf-imp-product-cell", className].filter(Boolean).join(" ");
  return (
    <As
      className={classes}
      title={[name, code].filter(Boolean).join(" · ")}
      {...rest}
    >
      <span className="epsf-imp-product-cell__name">{name || "—"}</span>
      {code ? (
        <span className="epsf-imp-product-cell__code">{code}</span>
      ) : null}
    </As>
  );
}

function isUsableEpsfProfilePictureUrl(url) {
  const value = String(url ?? "").trim();
  if (!value) return false;
  const lower = value.toLowerCase();
  if (lower.includes("avatar.png") || lower.includes("/images/avatar")) {
    return false;
  }
  return true;
}

/** Participant name + photo; click opens Participant Manager profile. */
export function EpsfParticipantProfileCell({ row }) {
  const navigate = useNavigate();
  const [imgFailed, setImgFailed] = useState(false);
  const participantId = String(
    row?.ParticipantId || row?.participantId || "",
  ).trim();
  const volunteerNo = sanitizeEpsfDisplayLabel(
    row?.VolunteerNo || row?.volunteerNo || "",
  );
  const screening = sanitizeEpsfDisplayLabel(
    row?.ScreeningId || row?.screeningId || "",
  );
  const name = sanitizeEpsfDisplayLabel(
    row?.ParticipantName || row?.participantName || "",
  );
  const idBadge = volunteerNo || screening;
  const photoUrl = String(
    row?.ProfilePictureUrl ||
      row?.profilePictureUrl ||
      row?.ProfileImage ||
      row?.profileImage ||
      "",
  ).trim();
  const showPhoto = isUsableEpsfProfilePictureUrl(photoUrl) && !imgFailed;
  const displayName =
    name ||
    sanitizeEpsfDisplayLabel(row?.ParticipantLabel || row?.participantLabel) ||
    idBadge ||
    "—";

  if (!participantId && displayName === "—") return "—";

  return (
    <button
      type="button"
      className="epsf-participant-cell"
      title={
        participantId
          ? `Open profile · ${[displayName, idBadge].filter(Boolean).join(" · ")}`
          : displayName
      }
      disabled={!participantId}
      onClick={() => {
        if (!participantId) return;
        navigate(`/participantmanager/participant/${participantId}`);
      }}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt=""
          className="epsf-participant-cell__avatar"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          className="epsf-participant-cell__avatar epsf-participant-cell__avatar--icon"
          aria-hidden="true"
        >
          <i className="fas fa-user" />
        </span>
      )}
      <span className="epsf-participant-cell__text">
        <span className="epsf-participant-cell__name">{displayName}</span>
        {idBadge && name ? (
          <span className="epsf-participant-cell__id">{idBadge}</span>
        ) : null}
      </span>
    </button>
  );
}

export function batchOptionLabel(row) {
  const code = String(row.ProductCode || row.productCode || "").trim();
  const name = sanitizeEpsfDisplayLabel(
    row.ProductName || row.productName || "",
  );
  const batch = String(row.BatchNumber || row.batchNumber || "").trim();
  const onHand = row.OnHand ?? row.onHand ?? 0;
  const exp = row.ExpiryDate || row.expiryDate;
  const expLabel = exp ? ` · exp ${String(exp).slice(0, 10)}` : "";
  const product = [code, name].filter(Boolean).join(" · ");
  const head = [batch, product].filter(Boolean).join(" · ");
  return `${head || "Batch"} (on hand ${onHand}${expLabel})`;
}

export const IMP_TXN_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "TxnAt", label: "Received" },
  { key: "BatchNumber", label: "Batch / Lot" },
  { key: "Quantity", label: "Qty" },
  { key: "KitCount", label: "Kits" },
  { key: "ReferenceNo", label: "Shipment / Ref" },
  { key: "ConditionCode", label: "Condition" },
  { key: "ReceiptStatus", label: "Status" },
];

export const IMP_TXN_DEFAULT_COLUMNS = [
  "Product",
  "TxnAt",
  "BatchNumber",
  "Quantity",
  "KitCount",
  "ReferenceNo",
  "ConditionCode",
  "ReceiptStatus",
];

/** Receive list: Condition + Status immediately after Received date. */
export const IMP_RECEIPT_FIELD_OPTIONS = [
  { key: "BatchNumber", label: "Batch number" },
  { key: "TxnAt", label: "Received" },
  { key: "ConditionCode", label: "Condition" },
  { key: "ReceiptStatus", label: "Status" },
  { key: "FormDue", label: "Forms" },
  { key: "Quantity", label: "Qty" },
  { key: "KitCount", label: "Kits" },
  { key: "ReferenceNo", label: "Shipment / Ref" },
];

export const IMP_RECEIPT_DEFAULT_COLUMNS = [
  "BatchNumber",
  "TxnAt",
  "ConditionCode",
  "ReceiptStatus",
  "FormDue",
  "Quantity",
  "ReferenceNo",
];

export function renderImpTxnCell(row, field) {
  if (field === "TxnAt") {
    return formatTxnWhen(row) || "—";
  }
  if (field === "Product") {
    return renderImpProductCell(row);
  }
  if (field === "BatchNumber") {
    return row.BatchNumber || row.batchNumber || "—";
  }
  if (field === "Quantity") {
    const qty = row.Quantity ?? row.quantity;
    const unit = row.UnitLabel || row.unitLabel || "";
    if (qty == null || qty === "") return "—";
    return unit ? `${qty} ${unit}` : String(qty);
  }
  if (field === "KitCount") {
    const n = row.KitCount ?? row.kitCount;
    if (n == null || n === "" || Number(n) === 0) return "—";
    return String(n);
  }
  if (field === "ReferenceNo") {
    return row.ReferenceNo || row.referenceNo || "—";
  }
  if (field === "ConditionCode") {
    return renderConditionBadge(row.ConditionCode || row.conditionCode);
  }
  if (field === "ReceiptStatus") {
    return renderReceiptStatusBadge(row.ReceiptStatus || row.receiptStatus);
  }
  const value = row[field];
  return value == null || value === "" ? "—" : String(value);
}

export function getImpTxnRowKey(row, index) {
  return String(row.ImpTxnId || row.impTxnId || index);
}

/** Shared columns for Dispense / Return / Destroy movement lists. */
export const IMP_MOVEMENT_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "TxnAt", label: "When" },
  { key: "ParticipantLabel", label: "Participant" },
  { key: "BatchNumber", label: "Batch / Lot" },
  { key: "Quantity", label: "Qty" },
  { key: "KitCount", label: "Kits" },
  { key: "ReturnedQuantity", label: "Return qty" },
  { key: "CreatedByName", label: "Recorded by" },
  { key: "ConditionCode", label: "Condition" },
  { key: "ReceiptStatus", label: "Status" },
  { key: "ReferenceNo", label: "Reference" },
];

/** Dispense list: Participant first; qty shows Units/Kits; no separate Kits column. */
export const IMP_DISPENSE_FIELD_OPTIONS = [
  { key: "ParticipantLabel", label: "Participant" },
  { key: "Product", label: "IMP" },
  { key: "TxnAt", label: "When" },
  { key: "Quantity", label: "Qty" },
  { key: "BatchNumber", label: "Batch No" },
  { key: "ReceiptStatus", label: "Status" },
  { key: "FormDue", label: "Forms" },
  { key: "ReturnedQuantity", label: "Return qty" },
  { key: "CreatedByName", label: "Dispensed by" },
  { key: "ReferenceNo", label: "Reference" },
  { key: "KitCount", label: "Kits" },
];

export const IMP_DISPENSE_DEFAULT_COLUMNS = [
  "ParticipantLabel",
  "Product",
  "TxnAt",
  "Quantity",
  "BatchNumber",
  "ReceiptStatus",
  "FormDue",
  "ReturnedQuantity",
  "CreatedByName",
];

export const IMP_RETURN_DEFAULT_COLUMNS = [
  "Product",
  "TxnAt",
  "ParticipantLabel",
  "BatchNumber",
  "Quantity",
  "KitCount",
  "ConditionCode",
  "ReceiptStatus",
  "CreatedByName",
];

export const IMP_DESTROY_DEFAULT_COLUMNS = [
  "Product",
  "TxnAt",
  "BatchNumber",
  "Quantity",
  "KitCount",
  "ReceiptStatus",
  "FormDue",
  "CreatedByName",
  "ReferenceNo",
];

function isKitTrackedRow(row) {
  const tracking = String(
    row?.InventoryTrackingMethod || row?.inventoryTrackingMethod || "",
  ).trim();
  if (tracking === "IndividualKit") return true;
  if (tracking === "BatchQuantity") return false;
  return Number(row?.KitCount ?? row?.kitCount ?? 0) > 0;
}

/** Batch qty → "20 Units"; kit-tracked → "20 Kits". */
export function formatImpQtyWithTracking(row, rawQty, { allowZero = false } = {}) {
  const n = Number(rawQty);
  if (!Number.isFinite(n)) return "—";
  if (!allowZero && n <= 0) return "—";
  if (n < 0) return "—";
  const isKit = isKitTrackedRow(row);
  const abs = Math.abs(n);
  const fmt = Number.isInteger(abs) ? String(abs) : String(Math.round(abs * 10000) / 10000);
  if (isKit) return `${fmt} ${abs === 1 ? "Kit" : "Kits"}`;
  return `${fmt} ${abs === 1 ? "Unit" : "Units"}`;
}

export function renderImpMovementCell(row, field) {
  if (field === "ParticipantLabel") {
    return <EpsfParticipantProfileCell row={row} />;
  }
  if (field === "Quantity") {
    return formatImpQtyWithTracking(row, row.Quantity ?? row.quantity);
  }
  if (field === "ReturnedQuantity") {
    return formatImpQtyWithTracking(
      row,
      row.ReturnedQuantity ?? row.returnedQuantity,
    );
  }
  if (field === "CreatedByName") {
    return row.CreatedByName || row.createdByName || "—";
  }
  if (field === "TxnAt") {
    return formatTxnWhen(row) || "—";
  }
  return renderImpTxnCell(row, field);
}

export function toApiDateString(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local Date + "HH:mm" → ISO-like local datetime string for API TxnAt. */
export function toApiDateTimeString(date, timeHHmm = "00:00") {
  if (!date) return null;
  const d = date instanceof Date ? new Date(date) : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const parts = String(timeHHmm || "00:00").split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}
